import React, { useState } from "react";
import { motion } from "framer-motion";
import { ChevronRight, UserPlus } from "lucide-react";
import { createCustomer } from "../../services/bookings";
import { Input } from "../../components/ui/input2";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../../components/ui/select";
import { Button } from "../../components/ui/Button";
import GlobalLoader from "../ui/GlobalLoader";
import { useToast } from "../../hooks/useToast";
import { cn } from "../../lib/utils";
import BackButton from "./BackButton";

interface Props {
  setSelectedCustomer: (customer: any) => void;
  setStep: (
    step:
      | "selection"
      | "customer"
      | "addCustomer"
      | "summary"
      | "payment"
      | "finalPayment"
  ) => void;
}

const skinTypes = ["Fair", "Medium", "Dark", "Sensitive"];

const AddCustomerStep: React.FC<Props> = ({ setSelectedCustomer, setStep }) => {
  const toast = useToast();
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    skin_type: "",
    landline: "",
    email: "",
    address: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const saveCustomer = async () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = "Full name is required";
    if (!form.mobile.trim()) errs.mobile = "Mobile number is required";
    if (!form.skin_type.trim()) errs.skin_type = "Skin type is required";

    // Email Validation
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = "Please enter a valid email address";
    }

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setLoading(true);
    try {
      const response = await createCustomer({ ...form });
      toast.success("Customer added successfully");
      setSelectedCustomer(response.data);
      setStep("summary");
    } catch (err: any) {
      if (err.type === "serverValidation") {
        setErrors(err.errors);
      } else {
        toast.error(err.message || "Failed to create customer");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#F9F9F9] -mx-8 -my-6">
      {/* List Header */}
      <div className="px-8 pt-8 pb-6 bg-white border-b border-gray-100 flex flex-col gap-1 mb-6">
        <h4 className="text-[17px] font-[600] text-[#402B69]">New Customer</h4>
        <p className="text-[11px] text-gray-400 font-medium">Capture details to create a unique profile</p>
      </div>

      <div className="flex-1 overflow-y-auto px-8 custom-scrollbar">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
          {/* Full Name */}
          <div className="space-y-1.5">
            <label className="text-[12px] font-[700] text-[#402B69] uppercase tracking-wider ml-1">Full Name *</label>
            <Input
              name="name"
              placeholder="e.g. John Doe"
              value={form.name}
              onChange={handleChange}
              className={cn(
                "h-12 py-0 px-4 rounded-xl border-gray-100 bg-white shadow-sm focus:border-[#885ABB] focus:ring-1 focus:ring-[#885ABB] transition-all font-medium text-sm",
                errors.name && "border-red-400 focus:ring-red-400"
              )}
            />
            {errors.name && <p className="text-[10px] text-red-500 font-bold ml-1">{errors.name}</p>}
          </div>

          {/* Mobile Number */}
          <div className="space-y-1.5">
            <label className="text-[12px] font-[700] text-[#402B69] uppercase tracking-wider ml-1">Mobile Number *</label>
            <Input
              name="mobile"
              placeholder="+44 7000 000000"
              value={form.mobile}
              onChange={handleChange}
              className={cn(
                "h-12 py-0 px-4 rounded-xl border-gray-100 bg-white shadow-sm focus:border-[#885ABB] focus:ring-1 focus:ring-[#885ABB] transition-all font-medium text-sm",
                errors.mobile && "border-red-400 focus:ring-red-400"
              )}
            />
            {errors.mobile && <p className="text-[10px] text-red-500 font-bold ml-1">{errors.mobile}</p>}
          </div>

          {/* Skin Type */}
          <div className="space-y-1.5">
            <label className="text-[12px] font-[700] text-[#402B69] uppercase tracking-wider ml-1">Skin Type *</label>
            <Select
              value={form.skin_type}
              onValueChange={(value) => {
                setForm({ ...form, skin_type: value });
                setErrors((prev) => ({ ...prev, skin_type: "" }));
              }}
            >
              <SelectTrigger className={cn(
                "h-12 rounded-xl border-gray-100 bg-white shadow-sm focus:ring-1 focus:ring-[#885ABB] transition-all font-medium text-sm px-4",
                errors.skin_type && "border-red-400"
              )}>
                <SelectValue placeholder="Identify complexion" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-100 rounded-xl shadow-2xl p-1 z-[200]">
                {skinTypes.map((t) => (
                  <SelectItem key={t} value={t} className="font-medium rounded-lg hover:bg-[#885ABB]/5 transition py-2">
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.skin_type && <p className="text-[10px] text-red-500 font-bold ml-1">{errors.skin_type}</p>}
          </div>

          {/* Landline */}
          <div className="space-y-1.5">
            <label className="text-[12px] font-[700] text-[#402B69] uppercase tracking-wider ml-1">Landline</label>
            <Input
              name="landline"
              placeholder="(Optional)"
              value={form.landline}
              onChange={handleChange}
              className="h-12 py-0 px-4 rounded-xl border-gray-100 bg-white shadow-sm focus:border-[#885ABB] focus:ring-1 focus:ring-[#885ABB] transition-all font-medium text-sm"
            />
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <label className="text-[12px] font-[700] text-[#402B69] uppercase tracking-wider ml-1">Email Address</label>
            <Input
              name="email"
              type="email"
              placeholder="example@mail.com"
              value={form.email}
              onChange={handleChange}
              className={cn(
                "h-12 py-0 px-4 rounded-xl border-gray-100 bg-white shadow-sm focus:border-[#885ABB] focus:ring-1 focus:ring-[#885ABB] transition-all font-medium text-sm",
                errors.email && "border-red-400 focus:ring-red-400"
              )}
            />
            {errors.email && <p className="text-[10px] text-red-500 font-bold ml-1">{errors.email}</p>}
          </div>

          {/* Address */}
          <div className="space-y-1.5 md:col-span-2">
            <label className="text-[12px] font-[700] text-[#402B69] uppercase tracking-wider ml-1">Physical Address</label>
            <Input
              name="address"
              placeholder="Street name, City, Postcode"
              value={form.address}
              onChange={handleChange}
              className="h-12 py-0 px-4 rounded-xl border-gray-100 bg-white shadow-sm focus:border-[#885ABB] focus:ring-1 focus:ring-[#885ABB] transition-all font-medium text-sm"
            />
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div className="px-8 py-6 bg-white border-t border-gray-50 flex items-center gap-4 mt-auto">
        <button
          onClick={() => setStep("customer")}
          className="px-8 h-12 rounded-xl text-gray-500 font-bold text-sm hover:bg-gray-50 transition-all border border-gray-100 uppercase tracking-widest"
        >
          Back
        </button>
        <button
          onClick={saveCustomer}
          disabled={loading}
          className="flex-1 h-12 bg-[#885ABB] text-white rounded-xl font-bold hover:bg-[#7748A4] transition-all shadow-lg shadow-[#885ABB]/20 flex items-center justify-center gap-2 group uppercase tracking-widest text-sm"
        >
          <span>{loading ? "Creating..." : "Create Customer"}</span>
          {!loading && <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />}
        </button>
      </div>

      {loading && (
        <div className="absolute inset-0 z-[100] flex items-center justify-center bg-white/60 backdrop-blur-[2px]">
          <GlobalLoader initialPageLoad={false} description="Creating customer..." compensateSidebar={false} />
        </div>
      )}
    </div>
  );
};

export default AddCustomerStep;
